import React, { useEffect, useState } from 'react';
import KoobButton from '@koob/ui/src/components/KoobButton';
import KoobModal from '@koob/ui/src/components/KoobModal';
import { RoomsSplitField } from '@koob/ui/src/features/pax/RoomsSplitField';
import { toDateString } from '@koob/dates';

interface RoomComposition {
  adults?: number;
  children?: number;
  childrenBirthdates?: (string | Date | null)[];
}

interface TripComposition {
  adults?: number;
  childrenBirthdates?: (string | Date | null)[];
}

interface SavedRoom {
  adults: number;
  children: number;
  // toDateString gives null for a birthdate it cannot read.
  childrenBirthdates: (string | null)[];
}

interface TripDayHotelRoomsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  emptyRoomMessage: React.ReactNode;
  overTripMessage: React.ReactNode;
  resetLabel: React.ReactNode;
  cancelLabel: React.ReactNode;
  saveLabel: React.ReactNode;
  rooms: RoomComposition[];
  tripComposition?: TripComposition;
  onSave: (rooms: SavedRoom[] | null) => void;
  /** Forwarded as-is to `RoomsSplitField`, scoped to `compose.stayRooms.*` by the caller. */
  t: (key: string, options?: Record<string, unknown>) => string;
}

const toSplit = (rooms: RoomComposition[] = []) =>
  rooms.map((room) => ({
    adults: room?.adults ?? 0,
    children: room?.children ?? room?.childrenBirthdates?.length ?? 0,
  }));

export default function TripDayHotelRoomsModal({
  open,
  setOpen,
  title,
  description,
  emptyRoomMessage,
  overTripMessage,
  resetLabel,
  cancelLabel,
  saveLabel,
  rooms: currentRooms,
  tripComposition,
  onSave,
  t,
}: TripDayHotelRoomsModalProps) {
  const adultsTotal = tripComposition?.adults ?? 0;
  const childrenBirthdates = tripComposition?.childrenBirthdates ?? [];
  const [rooms, setRooms] = useState(() => toSplit(currentRooms));

  useEffect(() => {
    if (open) {
      setRooms(toSplit(currentRooms));
    }
    // Resets the draft from the saved rooms each time the modal opens, not
    // while the user edits it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const adults = rooms.reduce((sum, room) => sum + room.adults, 0);
  const children = rooms.reduce((sum, room) => sum + room.children, 0);
  const hasEmptyRoom = rooms.some((room) => room.adults < 1);
  const isOverTrip =
    adults > adultsTotal || children > childrenBirthdates.length;
  const canSave = rooms.length > 0 && !hasEmptyRoom && !isOverTrip;

  const save = () => {
    let nextChild = 0;
    onSave(
      rooms.map((room) => {
        const birthdates = childrenBirthdates
          .slice(nextChild, nextChild + room.children)
          .map((birthdate) => toDateString(birthdate));
        nextChild += room.children;
        return {
          adults: room.adults,
          children: room.children,
          childrenBirthdates: birthdates,
        };
      }),
    );
    setOpen(false);
  };

  const reset = () => {
    onSave(null);
    setOpen(false);
  };

  return (
    <KoobModal open={open} setOpen={setOpen} title={title}>
      <p className="mb-4 text-sm text-gray-500">{description}</p>

      <RoomsSplitField
        adultsTotal={adultsTotal}
        childrenTotal={childrenBirthdates.length}
        rooms={rooms}
        onRoomsChange={setRooms}
        limitToTotals
        t={t}
      />

      {!canSave && (
        <p className="mt-3 text-sm text-red-500">
          {isOverTrip ? overTripMessage : emptyRoomMessage}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <KoobButton variant="ghost" onClick={reset}>
          {resetLabel}
        </KoobButton>
        <div className="flex gap-2">
          <KoobButton onClick={() => setOpen(false)}>{cancelLabel}</KoobButton>
          <KoobButton variant="primary" disabled={!canSave} onClick={save}>
            {saveLabel}
          </KoobButton>
        </div>
      </div>
    </KoobModal>
  );
}
